/**
 * The five register books (spec 8.4, CONTENT.md section 6, DISCUSSION-buku): the URL
 * slug of each book, its title, the minister's label ("Peneguh" on perkawinan), the
 * columns of the book screen in the physical book's order (with the TTL abbreviation for
 * headers only), the entry fields in book order for the entry mode and the entry view,
 * the enum labels, the ordering of volumes, the numbering rules (a duplicate blocks, a
 * gap warns), the rows-per-page setting, and the append-only marginal notes.
 *
 * Everything here is pure and dialect-neutral: the worker sorts with `urutanBukuSql`, the
 * screens render `KOLOM_BUKU` and `ISIAN_BUKU`, the tests read the rules directly.
 */
import { z } from 'zod'
import { formatPartialDateId, formatPartialDateInput } from './dates/partial'
import {
  BENTUK_PERKAWINAN,
  CARA_PERKAWINAN,
  JENIS_BAPTIS,
  JENIS_KELAMIN,
  type JENIS_SURAT,
  STATUS_PERKAWINAN_ENTRI,
  TABEL_REGISTER,
} from './schema/contract'
import { format } from './strings/index'
import ui from './strings/ui.id.json' with { type: 'json' }
import type { LocalWrite } from './sync/ops'
import { petaGalatZod } from './validators/galat'
import {
  BARIS_PER_HALAMAN_BAWAAN,
  barisPerHalamanSchema,
  type CatatanPinggir,
  catatanPinggirListSchema,
  tanggalDepanKetikSchema,
  tanggalKetikSchema,
  tanggalLampauKetikSchema,
} from './validators/kolom'
import { KOLOM_TIDAK_TERCATAT, KOLOM_WAJIB_BUKU, pastiSebelum } from './validators/register'

const teks = ui.buku
const pesan = ui.validasi

// ---------------------------------------------------------------------------
// Books
// ---------------------------------------------------------------------------

export type TabelRegister = (typeof TABEL_REGISTER)[number]
export type JenisSurat = (typeof JENIS_SURAT)[number]

/** The URL slug of each book: `/buku/komuni`, not `/buku/komuni_pertama`. */
export const JENIS_BUKU = ['baptis', 'komuni', 'krisma', 'perkawinan', 'kematian'] as const
export type JenisBuku = (typeof JENIS_BUKU)[number]

export const TABEL_DARI_JENIS: Readonly<Record<JenisBuku, TabelRegister>> = {
  baptis: 'baptis',
  komuni: 'komuni_pertama',
  krisma: 'krisma',
  perkawinan: 'perkawinan',
  kematian: 'kematian',
}

export const JENIS_DARI_TABEL: Readonly<Record<TabelRegister, JenisBuku>> = {
  baptis: 'baptis',
  komuni_pertama: 'komuni',
  krisma: 'krisma',
  perkawinan: 'perkawinan',
  kematian: 'kematian',
}

export function isJenisBuku(value: unknown): value is JenisBuku {
  return typeof value === 'string' && (JENIS_BUKU as readonly string[]).includes(value)
}

export function isTabelRegister(value: unknown): value is TabelRegister {
  return typeof value === 'string' && (TABEL_REGISTER as readonly string[]).includes(value)
}

/** "Buku Baptis", "Buku Komuni Pertama", ... */
export const JUDUL_BUKU: Readonly<Record<TabelRegister, string>> = teks.judul

/** The lowercase word for sentences: "Entri baptis no. 812", "Hapus entri komuni pertama no. 40?". */
export const KATA_BUKU: Readonly<Record<TabelRegister, string>> = teks.jenis

/** The letter printed from an entry of each book (section 11's `/cetak/:jenis/:id`). */
export const SURAT_BUKU: Readonly<Record<TabelRegister, JenisSurat>> = {
  baptis: 'surat_baptis',
  komuni_pertama: 'surat_komuni',
  krisma: 'surat_krisma',
  perkawinan: 'surat_perkawinan',
  kematian: 'surat_kematian',
}

/** Spec 8.4: the minister column is "Peneguh" on perkawinan and "Pelayan" elsewhere. */
export function labelPelayan(tabel: TabelRegister): string {
  return tabel === 'perkawinan' ? teks.kolom.peneguh : teks.kolom.pelayan
}

// ---------------------------------------------------------------------------
// Enum labels
// ---------------------------------------------------------------------------

export interface Opsi {
  value: string
  label: string
}

const opsiDari = (values: readonly string[], labels: Record<string, string>): Opsi[] =>
  values.map((value) => ({ value, label: labels[value] ?? value }))

export const OPSI_JENIS_BAPTIS: readonly Opsi[] = opsiDari(JENIS_BAPTIS, ui.enum.jenisBaptis)
export const OPSI_BENTUK_PERKAWINAN: readonly Opsi[] = opsiDari(
  BENTUK_PERKAWINAN,
  ui.enum.bentukPerkawinan,
)
export const OPSI_CARA_PERKAWINAN: readonly Opsi[] = opsiDari(
  CARA_PERKAWINAN,
  ui.enum.caraPerkawinan,
)
export const OPSI_STATUS_PERKAWINAN_ENTRI: readonly Opsi[] = opsiDari(
  STATUS_PERKAWINAN_ENTRI,
  ui.enum.statusPerkawinanEntri,
)
export const OPSI_JENIS_KELAMIN: readonly Opsi[] = opsiDari(JENIS_KELAMIN, ui.enum.jenisKelamin)
/** Religions offered in the parents' and spouses' fields; free text stays allowed. */
export const AGAMA: readonly string[] = teks.agama

const LABEL_ENUM: Readonly<Record<string, Readonly<Record<string, string>>>> = {
  jenis: ui.enum.jenisBaptis,
  bentuk: ui.enum.bentukPerkawinan,
  cara: ui.enum.caraPerkawinan,
  status: ui.enum.statusPerkawinanEntri,
  jenis_kelamin: ui.enum.jenisKelamin,
}

/** The label of an enum column's stored code, or the code itself. */
export function labelEnum(kolom: string, kode: string | null | undefined): string {
  if (kode === null || kode === undefined || kode === '') return ''
  return LABEL_ENUM[kolom]?.[kode] ?? kode
}

// ---------------------------------------------------------------------------
// Columns of the book screens (physical book order; the spine comes first)
// ---------------------------------------------------------------------------

export type BreakpointKolom = 'md' | 'lg' | 'xl' | '2xl'

export interface RegisterColumn {
  /** The formatter key (`teksSel`) and the column id. */
  key: string
  /** The header text; the full label goes to `judul` when the header abbreviates ("TTL"). */
  label: string
  headerLabel?: string
  /** CSS width; columns without one share the rest of the row. */
  width?: string
  align?: 'right'
  numeric?: boolean
  /** Names at 500. */
  strong?: boolean
  /** Hidden below this breakpoint on tablets and laptops (the phone scrolls sideways). */
  hideBelow?: BreakpointKolom
  /**
   * Two lines inside the constant row (a place and its date, a minister, witnesses): the
   * text wraps instead of being cut. `ttl` and `tempat_tanggal` put the place on the first
   * line and the date on the second, so the date always shows.
   */
  wrap?: boolean
}

/** Buku · Hal. · No.: sticky on the left of every book. */
export const KOLOM_SPINE: readonly RegisterColumn[] = [
  { key: 'buku', label: teks.kolom.buku, width: '3.25rem', numeric: true },
  { key: 'halaman', label: teks.kolom.halaman, width: '3.25rem', numeric: true },
  { key: 'nomor', label: teks.kolom.nomor, width: '3.75rem', numeric: true },
]

/*
 * Widths are budgeted for the content width with the sidebar open, not the window: the
 * breakpoints are the window's (md 768, lg 1024, xl 1280, 2xl 1440) but the table gets
 * 664, 720, 976 and 1136 px there, and never more than 1216 (main is 80rem). At every
 * tier the fixed columns leave the name at least about 10rem. The place and date of the
 * sacrament and its minister show from md up; mostly empty columns (wali, saksi, the
 * burial) wait for 2xl.
 */
export const KOLOM_BUKU: Readonly<Record<TabelRegister, readonly RegisterColumn[]>> = {
  baptis: [
    { key: 'nama', label: teks.kolom.nama, strong: true },
    { key: 'nama_baptis', label: teks.kolom.namaBaptis, width: '6rem', hideBelow: 'lg' },
    {
      key: 'ttl',
      label: teks.kolom.tempatTanggalLahir,
      headerLabel: teks.kolom.ttl,
      width: '7.5rem',
      hideBelow: 'xl',
      wrap: true,
    },
    { key: 'orang_tua', label: teks.kolom.orangTua, width: '8rem', hideBelow: 'xl', wrap: true },
    { key: 'wali', label: teks.kolom.waliBaptis, width: '8rem', hideBelow: '2xl', wrap: true },
    { key: 'tempat_tanggal', label: teks.kolom.tempatTanggalBaptis, width: '10.5rem', wrap: true },
    { key: 'pelayan', label: teks.kolom.pelayan, width: '8rem', wrap: true },
  ],
  komuni_pertama: [
    { key: 'nama', label: teks.kolom.nama, strong: true },
    { key: 'tanggal_lahir', label: teks.kolom.tanggalLahir, width: '7rem', hideBelow: 'xl' },
    { key: 'referensi_baptis', label: teks.kolom.rujukanBaptis, hideBelow: 'xl', wrap: true },
    { key: 'tanggal', label: teks.kolom.tanggal, width: '7rem' },
    { key: 'tempat', label: teks.kolom.tempat, width: '8rem', hideBelow: 'lg', wrap: true },
    { key: 'pelayan', label: teks.kolom.pelayan, width: '8rem', wrap: true },
  ],
  krisma: [
    { key: 'nama', label: teks.kolom.nama, strong: true },
    { key: 'nama_krisma', label: teks.kolom.namaKrisma, width: '6rem', hideBelow: 'lg' },
    { key: 'tanggal_lahir', label: teks.kolom.tanggalLahir, width: '7rem', hideBelow: 'xl' },
    {
      key: 'wali_krisma',
      label: teks.kolom.waliKrisma,
      width: '8rem',
      hideBelow: '2xl',
      wrap: true,
    },
    { key: 'referensi_baptis', label: teks.kolom.rujukanBaptis, hideBelow: 'xl', wrap: true },
    { key: 'tanggal', label: teks.kolom.tanggal, width: '7rem' },
    { key: 'pelayan', label: teks.kolom.pelayan, width: '8rem', wrap: true },
  ],
  perkawinan: [
    { key: 'pria_nama', label: teks.kolom.pria, strong: true },
    { key: 'wanita_nama', label: teks.kolom.wanita, strong: true },
    { key: 'tanggal', label: teks.kolom.tanggal, width: '7rem' },
    { key: 'bentuk', label: teks.kolom.bentuk, width: '8rem', hideBelow: 'xl', wrap: true },
    { key: 'saksi', label: teks.kolom.saksi, width: '9rem', hideBelow: '2xl', wrap: true },
    { key: 'pelayan', label: teks.kolom.peneguh, width: '8rem', hideBelow: 'lg', wrap: true },
  ],
  kematian: [
    { key: 'nama', label: teks.kolom.nama, strong: true },
    { key: 'tanggal_lahir', label: teks.kolom.tanggalLahir, width: '7rem', hideBelow: 'xl' },
    // The two-word headers set these widths (119 to 131 px at 13 px).
    { key: 'tanggal_meninggal', label: teks.kolom.tanggalMeninggal, width: '8.5rem' },
    {
      key: 'tempat_meninggal',
      label: teks.kolom.tempatMeninggal,
      width: '8.5rem',
      hideBelow: 'lg',
      wrap: true,
    },
    {
      key: 'tanggal_pemakaman',
      label: teks.kolom.tanggalPemakaman,
      width: '9.25rem',
      hideBelow: '2xl',
    },
    {
      key: 'tempat_pemakaman',
      label: teks.kolom.tempatPemakaman,
      width: '9.25rem',
      hideBelow: '2xl',
      wrap: true,
    },
    {
      key: 'referensi_baptis',
      label: teks.kolom.rujukanBaptis,
      width: '8rem',
      hideBelow: 'xl',
      wrap: true,
    },
  ],
}

/** The table's minimum width on phones, where every column shows and the row scrolls sideways. */
export const LEBAR_MIN_BUKU: Readonly<Record<TabelRegister, string>> = {
  baptis: '72rem',
  komuni_pertama: '60rem',
  krisma: '66rem',
  perkawinan: '62rem',
  kematian: '72rem',
}

/** The raw columns a book row carries beside the register head (what `register:list` selects). */
export const KOLOM_MENTAH: Readonly<Record<TabelRegister, readonly string[]>> = {
  baptis: [
    'nama',
    'nama_baptis',
    'tempat_lahir',
    'tanggal_lahir',
    'nama_ayah',
    'nama_ibu',
    'wali_1',
    'wali_2',
  ],
  komuni_pertama: ['nama', 'tanggal_lahir', 'referensi_baptis'],
  krisma: ['nama', 'nama_krisma', 'tanggal_lahir', 'wali_krisma', 'referensi_baptis'],
  perkawinan: ['pria_nama', 'wanita_nama', 'bentuk', 'saksi_1', 'saksi_2'],
  kematian: [
    'nama',
    'tanggal_lahir',
    'tanggal_meninggal',
    'tempat_meninggal',
    'tanggal_pemakaman',
    'tempat_pemakaman',
    'referensi_baptis',
  ],
}

export type KolomMentah = Readonly<Record<string, string | number | null | undefined>>

const s = (row: KolomMentah, key: string): string => {
  const v = row[key]
  return v === null || v === undefined ? '' : String(v)
}
const tgl = (row: KolomMentah, key: string): string => {
  const v = s(row, key)
  return v === '' ? '' : formatPartialDateId(v)
}
const gabung = (parts: readonly string[], pemisah: string): string =>
  parts.filter((p) => p !== '').join(pemisah)

/**
 * The text of one cell of the book screen from the raw row: "Agats, 12 Mei 1987" for TTL,
 * "Markus · Elisabet" for the parents, the enum label for bentuk. Empty when nothing is
 * recorded (the table shows an em dash).
 */
export function teksSel(key: string, row: KolomMentah): string {
  switch (key) {
    case 'ttl':
      return gabung([s(row, 'tempat_lahir'), tgl(row, 'tanggal_lahir')], ', ')
    case 'orang_tua':
      return gabung([s(row, 'nama_ayah'), s(row, 'nama_ibu')], ' · ')
    case 'wali':
      return gabung([s(row, 'wali_1'), s(row, 'wali_2')], ' · ')
    case 'saksi':
      return gabung([s(row, 'saksi_1'), s(row, 'saksi_2')], ' · ')
    case 'tempat_tanggal':
      return gabung([s(row, 'tempat'), tgl(row, 'tanggal')], ', ')
    case 'tanggal':
    case 'tanggal_lahir':
    case 'tanggal_meninggal':
    case 'tanggal_pemakaman':
      return tgl(row, key)
    case 'bentuk':
      return labelEnum('bentuk', s(row, 'bentuk'))
    default:
      return s(row, key)
  }
}

/** "Yufuwai, Yohanes": surname first, as every list shows names. A single name stays. */
export function namaTerbalik(nama: string | null | undefined): string {
  if (nama === null || nama === undefined) return ''
  const parts = nama.trim().split(/\s+/).filter(Boolean)
  if (parts.length < 2) return nama.trim()
  const marga = parts[parts.length - 1] as string
  return `${marga}, ${parts.slice(0, -1).join(' ')}`
}

/** The name line of an entry: the person, or "Pria · Wanita" for a marriage. */
export function namaEntri(tabel: TabelRegister, row: KolomMentah): string {
  if (tabel === 'perkawinan') return gabung([s(row, 'pria_nama'), s(row, 'wanita_nama')], ' · ')
  return s(row, 'nama')
}

// ---------------------------------------------------------------------------
// Entry fields, in book order (entry mode and the entry view share them)
// ---------------------------------------------------------------------------

export type JenisIsian = 'nomor' | 'teks' | 'tanggal' | 'radio' | 'pilihan' | 'pilih'

export interface RegisterField {
  name: string
  label: string
  kind: JenisIsian
  /** For `radio` and `pilih`. */
  options?: readonly Opsi[]
  /** For `pilihan` (a type-ahead over a local list): which list feeds it. */
  list?: 'tempat' | 'pelayan' | 'agama'
  /** The book requires it (`KOLOM_WAJIB_BUKU`, owner 2026-10-02); the rest say "(boleh kosong)". */
  required?: boolean
  /**
   * A required field an old book may not record: Mode Salin Buku offers its "Tidak tercatat
   * di buku" tick (`KOLOM_TIDAK_TERCATAT`). Not the number, the names or a marriage's status.
   */
  tidakTercatat?: boolean
  /**
   * Columns of twelve on laptops, filling whole rows. Mode Salin Buku fits 1280 × 720 without
   * scrolling, on a touch screen too: an optional field is at least three columns, so
   * "(boleh kosong)" stays on its label's line, as does a required field's "Tidak tercatat"
   * tick, and a choice is as wide as its buttons in one row (Jenis six). Catatan, the free
   * note, takes what its row leaves, and a row of its own where the form has the height
   * (Komuni); Baptis's last row is full (Jenis, No. KK) and a sixth row would not fit
   * with the error summary. Perkawinan has too many columns for that screen: its form scrolls
   * there.
   */
  span: number
  /** Columns of six on tablets; default 3 for short fields, 6 for text. */
  spanMd?: number
  num?: boolean
  placeholder?: string
  /** Kept when the form resets after a save (the page shares its minister and date). */
  lengket?: boolean
  /** A birth or death date: a date after today is refused (sapuan isian 3.5). */
  lampau?: boolean
  /**
   * The ceremony's date of Baptis, Komuni Pertama, Krisma and Perkawinan (release 17): it may lie
   * ahead up to a year, the parish preparing the certificate first; further is refused, and the
   * form says a date ahead quietly ("Tanggal ini belum tiba.").
   */
  depan?: boolean
}

const isian = teks.isian
const TANGGAL = { kind: 'tanggal' as const, num: true, placeholder: '12-05-1987', spanMd: 3 }
const TANGGAL_LAMPAU = { ...TANGGAL, lampau: true }
const TANGGAL_DEPAN = { ...TANGGAL, depan: true }
const RADIO_LP = { kind: 'radio' as const, options: OPSI_JENIS_KELAMIN, spanMd: 3 }
const AGAMA_FIELD = { kind: 'pilihan' as const, list: 'agama' as const, spanMd: 3 }

type IsianMentah = Omit<RegisterField, 'required' | 'tidakTercatat'>

/** The fields as the books lay them out; `required` and the tick come from the rule below. */
const ISIAN_MENTAH: Readonly<Record<TabelRegister, readonly IsianMentah[]>> = {
  baptis: [
    {
      name: 'nomor',
      label: isian.nomor,
      kind: 'nomor',
      span: 2,
      spanMd: 2,
      num: true,
    },
    { name: 'nama', label: isian.namaLengkap, kind: 'teks', span: 4, spanMd: 4 },
    { name: 'nama_baptis', label: isian.namaBaptis, kind: 'teks', span: 3, spanMd: 6 },
    { name: 'tempat_lahir', label: isian.tempatLahir, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'tanggal_lahir', label: isian.tanggalLahir, ...TANGGAL_LAMPAU, span: 3 },
    { name: 'jenis_kelamin', label: isian.jenisKelamin, ...RADIO_LP, span: 3 },
    { name: 'nama_ayah', label: isian.namaAyah, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'agama_ayah', label: isian.agamaAyah, ...AGAMA_FIELD, span: 3, lengket: true },
    { name: 'nama_ibu', label: isian.namaIbu, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'agama_ibu', label: isian.agamaIbu, ...AGAMA_FIELD, span: 3, lengket: true },
    { name: 'wali_1', label: isian.waliBaptis1, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'wali_2', label: isian.waliBaptis2, kind: 'teks', span: 3, spanMd: 3 },
    {
      name: 'tempat',
      label: isian.tempatBaptis,
      kind: 'pilihan',
      list: 'tempat',
      span: 3,
      spanMd: 3,
      lengket: true,
    },
    { name: 'tanggal', label: isian.tanggalBaptis, ...TANGGAL_DEPAN, span: 3, lengket: true },
    {
      name: 'pelayan',
      label: isian.pelayan,
      kind: 'pilihan',
      list: 'pelayan',
      span: 6,
      spanMd: 6,
      lengket: true,
    },
    {
      name: 'jenis',
      label: isian.jenis,
      kind: 'radio',
      options: OPSI_JENIS_BAPTIS,
      span: 6,
      spanMd: 6,
    },
    {
      name: 'nomor_kk_katolik',
      label: isian.nomorKkKatolik,
      kind: 'teks',
      span: 3,
      spanMd: 2,
      num: true,
    },
    { name: 'catatan', label: isian.catatan, kind: 'teks', span: 3, spanMd: 4 },
  ],
  komuni_pertama: [
    {
      name: 'nomor',
      label: isian.nomor,
      kind: 'nomor',
      span: 2,
      spanMd: 2,
      num: true,
    },
    { name: 'nama', label: isian.namaLengkap, kind: 'teks', span: 4, spanMd: 4 },
    { name: 'tanggal_lahir', label: isian.tanggalLahir, ...TANGGAL_LAMPAU, span: 3 },
    { name: 'referensi_baptis', label: isian.rujukanBaptis, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'tanggal', label: isian.tanggalKomuni, ...TANGGAL_DEPAN, span: 3, lengket: true },
    {
      name: 'tempat',
      label: isian.tempatKomuni,
      kind: 'pilihan',
      list: 'tempat',
      span: 3,
      spanMd: 3,
      lengket: true,
    },
    {
      name: 'pelayan',
      label: isian.pelayan,
      kind: 'pilihan',
      list: 'pelayan',
      span: 6,
      spanMd: 3,
      lengket: true,
    },
    { name: 'catatan', label: isian.catatan, kind: 'teks', span: 12, spanMd: 6 },
  ],
  krisma: [
    {
      name: 'nomor',
      label: isian.nomor,
      kind: 'nomor',
      span: 2,
      spanMd: 2,
      num: true,
    },
    { name: 'nama', label: isian.namaLengkap, kind: 'teks', span: 4, spanMd: 4 },
    { name: 'nama_krisma', label: isian.namaKrisma, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'tanggal_lahir', label: isian.tanggalLahir, ...TANGGAL_LAMPAU, span: 3 },
    { name: 'wali_krisma', label: isian.waliKrisma, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'referensi_baptis', label: isian.rujukanBaptis, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'tanggal', label: isian.tanggalKrisma, ...TANGGAL_DEPAN, span: 3, lengket: true },
    {
      name: 'tempat',
      label: isian.tempatKrisma,
      kind: 'pilihan',
      list: 'tempat',
      span: 3,
      spanMd: 3,
      lengket: true,
    },
    {
      name: 'pelayan',
      label: isian.pelayan,
      kind: 'pilihan',
      list: 'pelayan',
      span: 4,
      spanMd: 3,
      lengket: true,
    },
    { name: 'catatan', label: isian.catatan, kind: 'teks', span: 8, spanMd: 6 },
  ],
  perkawinan: [
    {
      name: 'nomor',
      label: isian.nomor,
      kind: 'nomor',
      span: 2,
      spanMd: 2,
      num: true,
    },
    { name: 'pria_nama', label: isian.namaPria, kind: 'teks', span: 4, spanMd: 4 },
    { name: 'pria_tanggal_lahir', label: isian.tanggalLahirPria, ...TANGGAL_LAMPAU, span: 3 },
    { name: 'pria_tempat_lahir', label: isian.tempatLahirPria, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'pria_agama', label: isian.agamaPria, ...AGAMA_FIELD, span: 3, lengket: true },
    { name: 'pria_nama_ayah', label: isian.namaAyahPria, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'pria_nama_ibu', label: isian.namaIbuPria, kind: 'teks', span: 3, spanMd: 3 },
    {
      name: 'pria_referensi_baptis',
      label: isian.rujukanBaptisPria,
      kind: 'teks',
      span: 3,
      spanMd: 3,
    },
    {
      name: 'wanita_nama',
      label: isian.namaWanita,
      kind: 'teks',
      span: 3,
      spanMd: 6,
    },
    { name: 'wanita_tanggal_lahir', label: isian.tanggalLahirWanita, ...TANGGAL_LAMPAU, span: 3 },
    {
      name: 'wanita_tempat_lahir',
      label: isian.tempatLahirWanita,
      kind: 'teks',
      span: 3,
      spanMd: 3,
    },
    { name: 'wanita_agama', label: isian.agamaWanita, ...AGAMA_FIELD, span: 3, lengket: true },
    { name: 'wanita_nama_ayah', label: isian.namaAyahWanita, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'wanita_nama_ibu', label: isian.namaIbuWanita, kind: 'teks', span: 3, spanMd: 3 },
    {
      name: 'wanita_referensi_baptis',
      label: isian.rujukanBaptisWanita,
      kind: 'teks',
      span: 3,
      spanMd: 3,
    },
    { name: 'tanggal', label: isian.tanggalPerkawinan, ...TANGGAL_DEPAN, span: 3, lengket: true },
    {
      name: 'tempat',
      label: isian.tempatPerkawinan,
      kind: 'pilihan',
      list: 'tempat',
      span: 3,
      spanMd: 3,
      lengket: true,
    },
    {
      name: 'pelayan',
      label: isian.peneguh,
      kind: 'pilihan',
      list: 'pelayan',
      span: 3,
      spanMd: 3,
      lengket: true,
    },
    { name: 'saksi_1', label: isian.saksi1, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'saksi_2', label: isian.saksi2, kind: 'teks', span: 3, spanMd: 3 },
    {
      name: 'bentuk',
      label: isian.bentuk,
      kind: 'pilih',
      options: OPSI_BENTUK_PERKAWINAN,
      span: 8,
      spanMd: 3,
    },
    {
      name: 'cara',
      label: isian.cara,
      kind: 'pilih',
      options: OPSI_CARA_PERKAWINAN,
      span: 4,
      spanMd: 3,
    },
    {
      name: 'status',
      label: isian.status,
      kind: 'pilih',
      options: OPSI_STATUS_PERKAWINAN_ENTRI,
      span: 5,
      spanMd: 3,
    },
    { name: 'dispensasi', label: isian.dispensasi, kind: 'teks', span: 3, spanMd: 3 },
    { name: 'catatan', label: isian.catatan, kind: 'teks', span: 4, spanMd: 6 },
  ],
  kematian: [
    {
      name: 'nomor',
      label: isian.nomor,
      kind: 'nomor',
      span: 2,
      spanMd: 2,
      num: true,
    },
    { name: 'nama', label: isian.namaLengkap, kind: 'teks', span: 4, spanMd: 4 },
    { name: 'tanggal_lahir', label: isian.tanggalLahir, ...TANGGAL_LAMPAU, span: 3 },
    { name: 'referensi_baptis', label: isian.rujukanBaptis, kind: 'teks', span: 3, spanMd: 3 },
    // Four columns each: "Tanggal pemakaman" and its "Tidak tercatat" tick share one line.
    { name: 'tanggal_meninggal', label: isian.tanggalMeninggal, ...TANGGAL_LAMPAU, span: 4 },
    { name: 'tempat_meninggal', label: isian.tempatMeninggal, kind: 'teks', span: 4, spanMd: 3 },
    { name: 'tanggal_pemakaman', label: isian.tanggalPemakaman, ...TANGGAL, span: 4 },
    { name: 'tempat_pemakaman', label: isian.tempatPemakaman, kind: 'teks', span: 4, spanMd: 3 },
    {
      name: 'pelayan',
      label: isian.pelayan,
      kind: 'pilihan',
      list: 'pelayan',
      span: 4,
      spanMd: 3,
      lengket: true,
    },
    { name: 'catatan', label: isian.catatan, kind: 'teks', span: 4, spanMd: 3 },
  ],
}

function isianDenganAturan(tabel: TabelRegister): RegisterField[] {
  const wajib = new Set(KOLOM_WAJIB_BUKU[tabel])
  const tanda = new Set<string>(KOLOM_TIDAK_TERCATAT[tabel])
  return ISIAN_MENTAH[tabel].map((f) => {
    const field: RegisterField = { ...f }
    if (wajib.has(f.name)) field.required = true
    if (tanda.has(f.name)) field.tidakTercatat = true
    return field
  })
}

/** Every book's entry fields in book order, each marked required or not by the book's rule. */
export const ISIAN_BUKU: Readonly<Record<TabelRegister, readonly RegisterField[]>> = {
  baptis: isianDenganAturan('baptis'),
  komuni_pertama: isianDenganAturan('komuni_pertama'),
  krisma: isianDenganAturan('krisma'),
  perkawinan: isianDenganAturan('perkawinan'),
  kematian: isianDenganAturan('kematian'),
}

const isianLanjut = teks.sakramenLanjut.isian

/**
 * The person's later sacraments typed on a baptism entry (owner, 2026-10-08; schema 13, the
 * margin note of a baptism register): every one optional, each date not after today. Ubah of
 * the entry and a ceremony's Baptis row ask them, under their own heading; Mode Salin Buku's
 * page does not (its fields fit one laptop screen). What SATU's books record of the person is
 * shown beside them and used while they are empty (`./sakramenLanjut`).
 */
export const ISIAN_SAKRAMEN_LANJUT: readonly RegisterField[] = [
  { name: 'komuni_tanggal', label: isianLanjut.komuniTanggal, ...TANGGAL_LAMPAU, span: 3 },
  { name: 'komuni_paroki', label: isianLanjut.komuniParoki, kind: 'teks', span: 3, spanMd: 3 },
  { name: 'krisma_tanggal', label: isianLanjut.krismaTanggal, ...TANGGAL_LAMPAU, span: 3 },
  { name: 'krisma_paroki', label: isianLanjut.krismaParoki, kind: 'teks', span: 3, spanMd: 3 },
  { name: 'perkawinan_tanggal', label: isianLanjut.perkawinanTanggal, ...TANGGAL_LAMPAU, span: 3 },
  {
    name: 'perkawinan_paroki',
    label: isianLanjut.perkawinanParoki,
    kind: 'teks',
    span: 3,
    spanMd: 3,
  },
  {
    name: 'perkawinan_pasangan',
    label: isianLanjut.perkawinanPasangan,
    kind: 'teks',
    span: 6,
    spanMd: 6,
  },
]

/** A book's fields beyond its page's (`ISIAN_BUKU`): Baptis's later sacraments, none elsewhere. */
export function isianLanjutBuku(tabel: TabelRegister): readonly RegisterField[] {
  return tabel === 'baptis' ? ISIAN_SAKRAMEN_LANJUT : []
}

// ---------------------------------------------------------------------------
// Volumes: "I", "II", ... sort as numbers, not as text
// ---------------------------------------------------------------------------

const SATUAN = ['', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX']
const PULUHAN = ['', 'X', 'XX', 'XXX', 'XL']

/** Roman numerals I to XLIX, in order. */
export const ROMAWI: readonly string[] = Array.from(
  { length: 49 },
  (_, i) => `${PULUHAN[Math.floor((i + 1) / 10)]}${SATUAN[(i + 1) % 10]}`,
)

/** The sort key of a volume: Roman numerals and plain numbers by value, anything else last. */
export function urutanBuku(buku: string): number {
  const b = buku.trim().toUpperCase()
  const romawi = ROMAWI.indexOf(b)
  if (romawi >= 0) return romawi + 1
  if (/^\d+[A-Z]?$/.test(b)) return Number.parseInt(b, 10)
  return 1000
}

export function bandingkanBuku(a: string, b: string): number {
  const d = urutanBuku(a) - urutanBuku(b)
  return d !== 0 ? d : a.localeCompare(b)
}

/** The same order as a SQL expression over a column (SQLite and Postgres). */
export function urutanBukuSql(kolom: string): string {
  const cases = ROMAWI.map((r, i) => `WHEN '${r}' THEN ${i + 1}`).join(' ')
  return `(CASE upper(trim(${kolom})) ${cases} ELSE (CASE WHEN ${kolom} GLOB '[0-9]*' THEN CAST(${kolom} AS INTEGER) ELSE 1000 END) END)`
}

// ---------------------------------------------------------------------------
// Numbering and pages
// ---------------------------------------------------------------------------

export interface PeriksaNomorInput {
  nomor: number
  buku: string
  /** Numbers already in this book (this device's copy plus this session's entries). */
  terpakai: ReadonlySet<number>
  /** The highest number before this entry, or null for an empty book. */
  terakhir: number | null
}

export type HasilNomor =
  | { jenis: 'ok' }
  /** Blocks: the same number twice in one book. */
  | { jenis: 'ganda'; pesan: string }
  /** Warns only: the book may really skip. */
  | { jenis: 'melompat'; pesan: string; dari: number; ke: number }

/** A duplicate in the same book blocks; a gap only warns (DISCUSSION-buku). */
export function periksaNomor(input: PeriksaNomorInput): HasilNomor {
  if (input.terpakai.has(input.nomor)) {
    return {
      jenis: 'ganda',
      pesan: format(teks.modeSalin.nomorGanda, { nomor: input.nomor, buku: input.buku }),
    }
  }
  if (input.terakhir !== null && input.nomor > input.terakhir + 1) {
    return {
      jenis: 'melompat',
      dari: input.terakhir,
      ke: input.nomor,
      pesan: format(teks.modeSalin.nomorMelompat, { dari: input.terakhir, ke: input.nomor }),
    }
  }
  return { jenis: 'ok' }
}

/** `pengaturan_paroki.baris_per_halaman` (JSON text or parsed) for one book; default 10. */
export function barisPerHalamanUntuk(pengaturan: unknown, tabel: TabelRegister): number {
  let value: unknown = pengaturan
  if (typeof value === 'string') {
    try {
      value = JSON.parse(value) as unknown
    } catch {
      return BARIS_PER_HALAMAN_BAWAAN
    }
  }
  const parsed = barisPerHalamanSchema.safeParse(value)
  if (!parsed.success) return BARIS_PER_HALAMAN_BAWAAN
  return parsed.data[tabel] ?? BARIS_PER_HALAMAN_BAWAAN
}

/** True once the page holds as many entries as the book has rows: the band offers the next page. */
export function halamanPenuh(jumlahDiHalaman: number, barisPerHalaman: number): boolean {
  return jumlahDiHalaman >= barisPerHalaman
}

// ---------------------------------------------------------------------------
// Marginal notes: appended, never rewritten (spec 8.4)
// ---------------------------------------------------------------------------

export const PENULIS_SISTEM: string = teks.catatanPinggir.sistem

/** The stored JSON (text or parsed) as a list; unreadable content reads as no notes. */
export function bacaCatatanPinggir(value: unknown): CatatanPinggir[] {
  let parsed: unknown = value
  if (typeof parsed === 'string') {
    if (parsed.trim() === '') return []
    try {
      parsed = JSON.parse(parsed) as unknown
    } catch {
      return []
    }
  }
  if (!Array.isArray(parsed)) return []
  return parsed.flatMap((item) => {
    const one = catatanPinggirListSchema.element.safeParse(item)
    return one.success ? [one.data] : []
  })
}

/** A new list with the note at the end; the given list is not touched. */
export function tambahCatatanPinggir(
  daftar: readonly CatatanPinggir[],
  catatan: CatatanPinggir,
): CatatanPinggir[] {
  return [...daftar, catatan]
}

/** A note the app writes itself, signed "Sistem". */
export function catatanSistem(tanggal: string, teksCatatan: string): CatatanPinggir {
  return { tanggal, teks: teksCatatan, oleh: PENULIS_SISTEM }
}

/** Notes in date order for the margin and the printed back page; ties keep their appended order. */
export function urutkanCatatanPinggir(daftar: readonly CatatanPinggir[]): CatatanPinggir[] {
  return daftar
    .map((c, i) => ({ c, i }))
    .sort((a, b) => (a.c.tanggal < b.c.tanggal ? -1 : a.c.tanggal > b.c.tanggal ? 1 : a.i - b.i))
    .map(({ c }) => c)
}

// ---------------------------------------------------------------------------
// The entry form: typed strings in, row columns out
// ---------------------------------------------------------------------------

export type IsianEntri = Record<string, string>

const teksOpsional = z
  .string()
  .trim()
  .transform((v) => (v === '' ? null : v))
const teksWajib = z.string().trim().min(1, pesan.wajib)
/** Empty says "Wajib diisi." alone: the checks after it do not run (`abort`). */
const wajibDulu = { error: pesan.wajib, abort: true } as const
const nomorSchema = z
  .string()
  .trim()
  .min(1, wajibDulu)
  .refine((v) => /^\d+$/.test(v) && Number(v) > 0, pesan.nomorBulat)
  .transform((v) => Number(v))
const halamanSchema = z
  .string()
  .trim()
  .refine((v) => v === '' || (/^\d+$/.test(v) && Number(v) > 0), pesan.nomorBulat)
  .transform((v) => (v === '' ? null : Number(v)))
/** A choice: one of its options, else "Pilih dari daftar." (never Zod's "Invalid input"). */
const kodeOpsional = (options: readonly Opsi[]) =>
  z
    .string()
    .trim()
    .refine((v) => v === '' || options.some((o) => o.value === v), pesan.pilihDariDaftar)
    .transform((v) => (v === '' ? null : v))
const kodeWajib = (options: readonly Opsi[]) =>
  z
    .string()
    .trim()
    .min(1, wajibDulu)
    .refine((v) => options.some((o) => o.value === v), pesan.pilihDariDaftar)
/** A date the book requires: empty says "Wajib diisi.", the rest as the optional date. */
const tanggalWajib = (tanggal: z.ZodType<string | null, string>) =>
  z.string().trim().min(1, pesan.wajib).pipe(tanggal)

function skemaKolom(field: RegisterField): z.ZodType<unknown, string> {
  const wajib = field.required === true
  switch (field.kind) {
    case 'nomor':
      return nomorSchema
    case 'tanggal': {
      const tanggal =
        field.lampau === true
          ? tanggalLampauKetikSchema
          : field.depan === true
            ? tanggalDepanKetikSchema
            : tanggalKetikSchema
      return wajib ? tanggalWajib(tanggal) : tanggal
    }
    case 'radio':
    case 'pilih':
      return wajib ? kodeWajib(field.options ?? []) : kodeOpsional(field.options ?? [])
    default:
      return wajib ? teksWajib : teksOpsional
  }
}

/**
 * A date of the book that must not precede another: the later column, the earlier one, and the
 * sentence said on the later one, which names the book's own date (spec 16; the test sweep of
 * 2026-10-04: Buku Kematian read "Tanggal baptis lebih awal…").
 */
type UrutanTanggal = readonly [nanti: string, awal: string, pesan: string]

const URUTAN_TANGGAL: Readonly<Record<TabelRegister, readonly UrutanTanggal[]>> = {
  baptis: [['tanggal', 'tanggal_lahir', pesan.baptisSebelumLahir]],
  komuni_pertama: [['tanggal', 'tanggal_lahir', pesan.komuniSebelumLahir]],
  krisma: [['tanggal', 'tanggal_lahir', pesan.krismaSebelumLahir]],
  perkawinan: [
    ['tanggal', 'pria_tanggal_lahir', pesan.perkawinanSebelumLahirPria],
    ['tanggal', 'wanita_tanggal_lahir', pesan.perkawinanSebelumLahirWanita],
  ],
  kematian: [
    ['tanggal_meninggal', 'tanggal_lahir', pesan.meninggalSebelumLahir],
    // The burial after the death; without a death date, after the birth.
    ['tanggal_pemakaman', 'tanggal_meninggal', pesan.pemakamanSebelumMeninggal],
    ['tanggal_pemakaman', 'tanggal_lahir', pesan.pemakamanSebelumLahir],
  ],
}

// ---------------------------------------------------------------------------
// "Tidak tercatat di buku" (owner, 2026-10-02)
// ---------------------------------------------------------------------------

/**
 * The form's field that holds the ticked columns, as one text ("nama_ayah,wali_1"); the
 * row's `tidak_tercatat` holds them as a JSON list, null when none is ticked.
 */
export const ISIAN_TIDAK_TERCATAT = 'tidak_tercatat'

/** The names as a list of text, from the stored JSON (text or parsed) or a list; else none. */
function daftarNama(value: unknown): string[] {
  let parsed: unknown = value
  if (typeof parsed === 'string') {
    if (parsed.trim() === '') return []
    try {
      parsed = JSON.parse(parsed) as unknown
    } catch {
      return []
    }
  }
  return Array.isArray(parsed) ? parsed.filter((n): n is string => typeof n === 'string') : []
}

/** The book's columns that may be ticked among `nama`, each once, in the book's order. */
function urutBuku(tabel: TabelRegister, nama: Iterable<string>): string[] {
  const ada = new Set(nama)
  return KOLOM_TIDAK_TERCATAT[tabel].filter((k) => ada.has(k))
}

/** The ticked columns of a stored entry (its `tidak_tercatat`), in the book's order. */
export function bacaTidakTercatat(tabel: TabelRegister, value: unknown): string[] {
  return urutBuku(tabel, daftarNama(value))
}

/** The ticked columns the form holds (`ISIAN_TIDAK_TERCATAT`), in the book's order. */
export function daftarTidakTercatat(tabel: TabelRegister, teks: string | undefined): string[] {
  return urutBuku(
    tabel,
    (teks ?? '').split(',').map((n) => n.trim()),
  )
}

/** The form's text for a list of ticked columns. */
export function teksTidakTercatat(daftar: readonly string[]): string {
  return daftar.join(',')
}

/** The book list's "Tidak lengkap": the entry has a column ticked as not in the book. */
export function entriTidakLengkap(value: unknown): boolean {
  return daftarNama(value).length > 0
}

/**
 * Ubah offers the ticks only on an entry copied from a book: one that has a tick already, or,
 * saved before the rule, a required column left empty (it can then be filled or ticked). A
 * new sacrament's entry never gains a gap later.
 */
export function ubahBolehTidakTercatat(tabel: TabelRegister, row: KolomMentah): boolean {
  if (bacaTidakTercatat(tabel, row.tidak_tercatat).length > 0) return true
  return KOLOM_TIDAK_TERCATAT[tabel].some((k) => s(row, k).trim() === '')
}

// ---------------------------------------------------------------------------
// The form's schema
// ---------------------------------------------------------------------------

const skemaCache = new Map<TabelRegister, z.ZodType<Record<string, unknown>, IsianEntri>>()

/**
 * The Zod schema of a book's entry form: every field a string as typed (empty for
 * nothing), the output the row's columns (numbers, canonical dates, null for empty), with
 * the book reference (`buku`, `halaman`) and the cross-field date check. A field the book
 * requires must be filled ("Wajib diisi.") unless the form ticks it as not recorded in the
 * book (`tidak_tercatat`, only the book's `KOLOM_TIDAK_TERCATAT`): a ticked field saves
 * empty whatever it holds, and the output's `tidak_tercatat` lists the ticks in book order
 * (null for none). Every problem is reported at once, field by field in the book's order,
 * the date check last.
 */
export function skemaIsian(tabel: TabelRegister): z.ZodType<Record<string, unknown>, IsianEntri> {
  const cached = skemaCache.get(tabel)
  if (cached !== undefined) return cached
  const kolom: [string, z.ZodType<unknown, string>][] = [
    ['buku', teksWajib],
    ['halaman', halamanSchema],
    ...[...ISIAN_BUKU[tabel], ...isianLanjutBuku(tabel)].map(
      (f): [string, z.ZodType<unknown, string>] => [f.name, skemaKolom(f)],
    ),
  ]
  const urutan = URUTAN_TANGGAL[tabel]
  const schema = z
    .custom<IsianEntri>((v) => typeof v === 'object' && v !== null)
    .transform((isian, ctx) => {
      const tanda = daftarTidakTercatat(tabel, isian[ISIAN_TIDAK_TERCATAT])
      const dicentang = new Set(tanda)
      const out: Record<string, unknown> = {}
      let lolos = true
      for (const [nama, skema] of kolom) {
        if (dicentang.has(nama)) {
          out[nama] = null
          continue
        }
        const raw = isian[nama]
        // A problem the field's schema left unworded is worded in the app's words.
        const hasil = skema.safeParse(typeof raw === 'string' ? raw : '', { error: petaGalatZod })
        if (hasil.success) {
          out[nama] = hasil.data
          continue
        }
        lolos = false
        for (const issue of hasil.error.issues) {
          ctx.addIssue({ code: 'custom', path: [nama, ...issue.path], message: issue.message })
        }
      }
      // One sentence per date: the first pair it breaks.
      const disebut = new Set<string>()
      for (const [nanti, awal, kalimat] of urutan) {
        const a = out[nanti]
        const b = out[awal]
        if (disebut.has(nanti) || typeof a !== 'string' || typeof b !== 'string') continue
        if (!pastiSebelum(a, b)) continue
        lolos = false
        disebut.add(nanti)
        ctx.addIssue({ code: 'custom', path: [nanti], message: kalimat })
      }
      out[ISIAN_TIDAK_TERCATAT] = tanda.length === 0 ? null : tanda
      return lolos ? out : z.NEVER
    }) as unknown as z.ZodType<Record<string, unknown>, IsianEntri>
  skemaCache.set(tabel, schema)
  return schema
}

/**
 * The schema of some of a book's fields with the book reference (`buku`, `halaman`), each
 * checked as the entry form checks it, without the cross-field date check: a ceremony batch's
 * first step (spec 2026-10-02 upacara bersama, 3.2), the fields every row shares.
 */
export function skemaSebagian(
  tabel: TabelRegister,
  nama: readonly string[],
): z.ZodType<Record<string, unknown>, IsianEntri> {
  const pilih = new Set(nama)
  const bentuk: Record<string, z.ZodType<unknown, string>> = {
    buku: teksWajib,
    halaman: halamanSchema,
  }
  for (const f of ISIAN_BUKU[tabel]) if (pilih.has(f.name)) bentuk[f.name] = skemaKolom(f)
  return z.object(bentuk) as unknown as z.ZodType<Record<string, unknown>, IsianEntri>
}

/** The empty form of a book with the context filled in. */
export function isianKosong(
  tabel: TabelRegister,
  konteks: { buku: string; halaman: number | null; nomor: number },
): IsianEntri {
  const out: IsianEntri = {
    buku: konteks.buku,
    halaman: konteks.halaman === null ? '' : String(konteks.halaman),
  }
  for (const field of [...ISIAN_BUKU[tabel], ...isianLanjutBuku(tabel)]) {
    out[field.name] = field.name === 'nomor' ? String(konteks.nomor) : ''
  }
  if (tabel === 'perkawinan') out.status = 'sah'
  out[ISIAN_TIDAK_TERCATAT] = ''
  return out
}

/**
 * The next form after a save: the context advanced, sticky fields kept, the rest cleared,
 * the ticks too (a gap belongs to one entry of the book, not to the page).
 */
export function isianBerikutnya(
  tabel: TabelRegister,
  sebelumnya: IsianEntri,
  konteks: { buku: string; halaman: number | null; nomor: number },
): IsianEntri {
  const out = isianKosong(tabel, konteks)
  for (const field of ISIAN_BUKU[tabel]) {
    if (field.lengket === true) out[field.name] = sebelumnya[field.name] ?? ''
  }
  return out
}

/** A stored row as the form types it (for the entry view's Ubah). */
export function isianDariBaris(tabel: TabelRegister, row: KolomMentah): IsianEntri {
  const out: IsianEntri = {
    buku: s(row, 'buku'),
    halaman: s(row, 'halaman'),
  }
  for (const field of [...ISIAN_BUKU[tabel], ...isianLanjutBuku(tabel)]) {
    const v = s(row, field.name)
    out[field.name] = field.kind === 'tanggal' ? formatPartialDateInput(v) : v
  }
  out[ISIAN_TIDAK_TERCATAT] = teksTidakTercatat(bacaTidakTercatat(tabel, row.tidak_tercatat))
  return out
}

/**
 * The columns of the row from the parsed form: kematian keeps its common `tanggal` equal
 * to the date of death so the book sorts and reports by it.
 */
export function kolomDariIsian(
  tabel: TabelRegister,
  out: Record<string, unknown>,
): Record<string, unknown> {
  const kolom: Record<string, unknown> = { ...out }
  if (tabel === 'kematian') kolom.tanggal = out.tanggal_meninggal ?? null
  return kolom
}

/**
 * A new entry as Mode Salin Buku writes it (a ceremony batch writes each of its entries so):
 * the parish, the row's columns (`kolomDariIsian`, with the minister's `pelayan_id`) and no
 * margin notes.
 */
export function tulisanEntriBaru(
  tabel: TabelRegister,
  id: string,
  parokiId: string | null,
  kolom: Readonly<Record<string, unknown>>,
): LocalWrite {
  return {
    table: tabel,
    row_id: id,
    fields: { paroki_id: parokiId, ...kolom, catatan_pinggir: [] },
  }
}

/**
 * The columns whose value differs from the stored row: an edit's op fields. The ticks
 * compare as lists (the row holds JSON text, the form's output a list or null).
 */
export function perubahanEntri(
  sekarang: KolomMentah,
  baru: Record<string, unknown>,
): Record<string, unknown> {
  const fields: Record<string, unknown> = {}
  for (const [kolom, nilai] of Object.entries(baru)) {
    const lama = sekarang[kolom] ?? null
    const kini = nilai ?? null
    if (kolom === ISIAN_TIDAK_TERCATAT) {
      if (daftarNama(lama).join(',') !== daftarNama(kini).join(',')) fields[kolom] = kini
      continue
    }
    if (String(lama ?? '') !== String(kini ?? '')) fields[kolom] = kini
  }
  return fields
}
